import React from 'react';
import lodashCompact from 'lodash/fp/compact';
import lodashTimes from 'lodash/fp/times';
import lodashGet from 'lodash/fp/get';
import lodashIdentity from 'lodash/fp/identity';
import lodashFlatten from 'lodash/fp/flatten';
import moment from 'moment';

import typeCheck from 'common/util/typeCheck';
import slugify from 'common/util/slugify';
import makeFullName from 'common/util/makeFullName';
import { stateOptionItems } from 'common/util/stateCodes';
import nameSuffixOptionItems from 'client/helpers/nameSuffixOptionItems';
import petOptionItems, {
  dogBreedsOptionItems,
  PET_OPTION_VALUE_DOG,
} from 'common/petOptionItems';
import vehicleTypeOptionItems from 'common/vehicleTypeOptionItems';

import {
  INCOME_VERIFICATION_STATUS_COMPLETED,
} from 'common/incomeVerificationConstants';

import {
  RENTAL_APPLICATION_PAYSTUB_FILES_COUNT,
  RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT,
  PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
  getProofOfIncomeQuestions,
  ProofOfIncomeWarningMessage,
  makePoiDocFilePath,
} from 'common/householdApplication/incomeHelpers';

import {
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED,
  HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED,
} from 'common/householdConstants';

import {
  SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
  SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
  SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_CHECKBOX,
  SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
  SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
} from 'client/components/SrFormikField';
import {
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_DEPENDENTS,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION,
  SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_PETS,
} from 'client/components/SrApplicationAssemblyLine/assemblyLineStepIds';

import SrBox from 'client/components/SrBox';
import SrIcon from 'client/components/SrIcon';
import SrText from 'client/components/SrText';
import {
  showFieldIf,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

const defaultTextPlaceholder = 'Write here...';
const maxChildrenCount = 6;
const maxPetCount = 6;
const maxVehicleCount = 5;
const maxTotalIncomeVerification = 6;

export const FIELDS_GROUPS_STEPS = {
  [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_DEPENDENTS]: lodashFlatten(lodashTimes(
    (index) => [`children[${index}].name`, `children[${index}].age`, `children[${index}].relationship`],
    maxChildrenCount,
  )),
  [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_INCOME_VERIFICATION]: [
    'explanationOfIncome',
    ...lodashTimes((index) => `paystubPhotos[${index}]`, maxTotalIncomeVerification),
  ],
  [SR_APPLICATION_ASSEMBLY_LINE_STEP_ID_PETS]: lodashFlatten(lodashTimes(
    (index) => [
      `pets[${index}].name`,
      `pets[${index}].type`,
      `pets[${index}].weight`,
      `pets[${index}].breed`,
      `pets[${index}].color`,
      `pets[${index}].age`,
      `pets[${index}].gender`,
      `pets[${index}].neutered`,
      `pets[${index}].declawed`,
      `pets[${index}].rabiesShotsCurrent`,
      `pets[${index}].isEsa`,
      `pets[${index}].esaCertificate`,
      `pets[${index}].photo`,
    ],
    maxPetCount,
  )),
};

export default function getRentalApplicationFormFields({
  formType,
  formValues,
  setFieldValue,
  validateEmail,
  deleteAdditionalProofOfIncomeFile,
  renterProfile,
  listing,
  tenantScreeningCriteria,
  hasBuyersAgent,
  fieldgroup,
  uploadAs,
  isGuarantor,
  onChangeEmploymentStatus,
  onPetCountChange,
  onVehicleCountChange,
  onChildrenCountChange,
  onShowInviteRoommateModal,
  isLeadRenter,
  incomeVerificationStatus,
  incomeVerificationProviderEnabled,
  disableSsnFieldsIfNoSsn,
}) {
  typeCheck('formType::NonEmptyString', formType);
  typeCheck('formValues::Object', formValues);
  typeCheck('setFieldValue::Maybe Function', setFieldValue);
  typeCheck('validateEmail::Maybe Function', validateEmail);
  typeCheck('deleteAdditionalProofOfIncomeFile::Maybe Function', deleteAdditionalProofOfIncomeFile);
  typeCheck('renterProfile::Object', renterProfile);
  typeCheck('hasBuyersAgent::Maybe Boolean', hasBuyersAgent);
  typeCheck('fieldgroup::NonEmptyArray', fieldgroup);
  typeCheck('uploadAs::Maybe PositiveNumber', uploadAs);
  typeCheck('isGuarantor::Maybe Boolean', isGuarantor);
  typeCheck('onChangeEmploymentStatus::Maybe Function', onChangeEmploymentStatus);
  typeCheck('onPetCountChange::Maybe Function', onPetCountChange);
  typeCheck('onVehicleCountChange::Maybe Function', onVehicleCountChange);
  typeCheck('onChildrenCountChange::Maybe Function', onChildrenCountChange);
  typeCheck('onShowInviteRoommateModal::Maybe Function', onShowInviteRoommateModal);
  typeCheck('isLeadRenter::Maybe Boolean', isLeadRenter);
  typeCheck('incomeVerificationStatus::Maybe String', incomeVerificationStatus);
  typeCheck('incomeVerificationProviderEnabled::Maybe Boolean', incomeVerificationProviderEnabled);

  const shouldAskForPreviousEmployment = (
    formValues.currentJobStartDate
    && moment(formValues.currentJobStartDate).isValid()
    && moment(formValues.currentJobStartDate).isAfter(moment().subtract(1, 'year'))
  );

  const shouldHideCurrentEmploymentQuestions = (
    !isGuarantor
    && (
      !formValues.hasSourceOfIncome
      || formValues.employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED
      || !formValues.employmentStatus
    )
  );

  const shouldDisplayStatedYearlyIncome = (
    formValues.employmentStatus === HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED
    && formValues.hasSourceOfIncome
  );

  const incomeVerificationProviderActive = (
    incomeVerificationProviderEnabled && formValues.incomeVerificationVOIE != null
  );

  const rentalApplicationFormFields = lodashCompact([
    {
      id: 'firstName',
      title: 'First Name',
      validations: ['required', 'personName'],
      type: 'short_text',
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
    },
    {
      id: 'middleName',
      title: 'Middle Name',
      type: 'short_text',
      validations: ['personName'],
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
    },
    {
      id: 'lastName',
      title: 'Last Name',
      validations: ['required', 'personName'],
      type: 'short_text',
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
    },
    {
      id: 'nameSuffix',
      title: 'Name Suffix (Optional)',
      properties: {
        placeholder: '...Choose',
        enableResetOption: true,
        choices: nameSuffixOptionItems,
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`,
      },
      type: 'select',
    },
    {
      id: 'email',
      title: 'Email',
      validations: ['required', 'email'],
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
        validate: validateEmail,
      },
      type: 'email',
    },
    {
      id: 'phone',
      title: 'Phone',
      validations: ['required', 'phone'],
      properties: {
        pattern: '[0-9]*',
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
      type: 'phone',
    },
    {
      id: 'dateOfBirth',
      title: 'Date of Birth',
      properties: {
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'date', 'dateOfBirth'],
      type: 'date_input',
    },
    {
      id: 'ssn',
      title: 'SSN/ITIN 🔒',
      properties: {
        pattern: '[0-9]*',
        placeholder: formValues.noSsn
          ? 'I don’t have one'
          : 'XXXXXXXXX',
        focusInputOnClickContainer: true,
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} emoticon`,
        onChange: () => {
          setFieldValue('noSsn', false);
        },
        disabled: disableSsnFieldsIfNoSsn
          ? formValues.noSsn
          : false,
      },
      validations: formValues.noSsn ? [] : ['required', 'ssn'],
      type: 'ssn',
    },
    {
      id: 'confirmSsn',
      title: 'Confirm SSN/ITIN 🔒',
      properties: {
        pattern: '[0-9]*',
        placeholder: formValues.noSsn
          ? 'I don’t have one'
          : 'XXXXXXXXX',
        focusInputOnClickContainer: true,
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} emoticon`,
        disabled: disableSsnFieldsIfNoSsn
          ? formValues.noSsn
          : false,
      },
      validations: formValues.noSsn ? [] : ['required', 'ssn', 'sameSsn:ssn'],
      type: 'ssn',
    },
    {
      id: 'noSsn',
      validations: formValues.ssn ? [] : ['required'],
      type: 'simple_checkbox',
      properties: {
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} noSsn`,
        children: 'I don\'t have a Social Security Number or Individual Taxpayer Identification Number',
        onChange: (noSsn) => {
          if (noSsn) {
            setFieldValue('ssn', '');
            setFieldValue('confirmSsn', '');
          }
        },
      },
    },
    {
      id: 'currentAddressStreetName',
      title: 'Street',
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
      validations: ['required', 'streetName'],
      type: 'short_text',
    },
    {
      id: 'currentAddressApartmentUnit',
      title: 'Apt #/Unit',
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
      type: 'short_text',
    },
    {
      id: 'currentAddressCity',
      title: 'City',
      properties: {
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        placeholder: defaultTextPlaceholder,
      },
      validations: ['required', 'city'],
      type: 'short_text',
    },
    {
      id: 'currentAddressStateCode',
      title: 'State',
      properties: {
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`,
        choices: stateOptionItems,
      },
      validations: ['required'],
      type: 'select',
    },
    {
      id: 'currentAddressZip',
      title: 'Zip Code',
      properties: {
        pattern: '[0-9]*',
        placeholder: '45123',
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'zipCode'],
      type: 'zipCode',
    },
    {
      id: 'currentLandlordName',
      title: 'Landlord Name',
      label: (
        <>
          Sunroom will reach out to your landlord to verify your rental info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      validations: ['required'],
      type: 'short_text',
    },
    {
      id: 'currentLandlordEmail',
      title: 'Landlord Email',
      label: (
        <>
          Sunroom will reach out to your landlord to verify your rental info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      validations: ['required', 'email'],
      type: 'email',
    },
    {
      id: 'currentLandlordPhone',
      title: 'Landlord Phone',
      label: (
        <>
          Sunroom will reach out to your landlord to verify your rental info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      validations: ['required', 'phone'],
      type: 'phone',
    },
    {
      id: 'currentMonthlyRent',
      title: 'Monthly Rent',
      properties: {
        pattern: '[0-9]*',
        placeholder: '800',
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required'],
      type: 'currency',
    },
    {
      id: 'currentRentalMoveInDate',
      title: 'Move-in Date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'currentRentalMoveOutDate',
      title: 'Move-out Date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'date'],
      type: 'date_input',
    },
    {
      id: 'currentRentalReasonMovingOut',
      title: 'Reason for Moving',
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      validations: ['required'],
      type: 'long_text',
    },
    {
      id: 'moveInDate',
      title: 'Move-in Date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'Move-in Date',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: isLeadRenter ? ['required', 'date'] : [],
      type: 'date_input',
    },
    {
      id: 'leaseTerm',
      validations: isLeadRenter ? ['required', 'positiveNumber', 'leaseTerm'] : [],
      title: 'Lease Length (months)',
      properties: {
        pattern: '[0-9]*',
        placeholder: '12',
        onlyAcceptNumbers: true,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        minLeaseMonths: listing.minLeaseMonths,
        maxLeaseMonths: listing.maxLeaseMonths,
      },
      type: 'short_text',
    },
    {
      id: 'hasSourceOfIncome',
      title: 'Do you have any source of income?',
      properties: {
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
      },
      validations: ['required'],
      type: 'yes_no',
    },
    hideFieldIf(!formValues.hasSourceOfIncome, {
      id: 'employmentStatus',
      title: 'Are you employed?',
      properties: {
        multipleSelection: false,
        choices: [
          {
            label: 'Yes',
            value: HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_EMPLOYED,
          },
          {
            label: 'No',
            value: HOUSEHOLD_APPLICATION_EMPLOYMENT_STATUS_NOT_EMPLOYED,
          },
        ],
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_CHECKBOX,
        onChange: onChangeEmploymentStatus,
      },
      validations: ['required'],
      type: 'multiple_choice',
    }),
    showFieldIf(shouldDisplayStatedYearlyIncome, {
      id: 'statedYearlyIncome',
      title: 'Yearly Income',
      type: 'currency',
      validations: ['required'],
      properties: {
        placeholder: 'Write here...',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
    }),
    hideFieldIf(formValues.hasSourceOfIncome !== false, {
      id: 'payRentExplanation',
      title: 'How are you going to pay the rent?',
      properties: {
        placeholder: 'Write here...',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      validations: ['required'],
      type: 'long_text',
    }),
    !incomeVerificationProviderActive && hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobCompany',
      title: 'Company Name',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required'],
      type: 'short_text',
    }),
    !incomeVerificationProviderActive && hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobEmployerMailingAddress',
      title: 'Mailing Address',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required'],
      type: 'short_text',
    }),
    !incomeVerificationProviderActive && hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobPosition',
      title: 'Job Title',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required'],
      type: 'short_text',
    }),
    hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobSupervisorName',
      title: 'Supervisor Name',
      label: !incomeVerificationProviderActive && (
        <>
          Sunroom will reach out to your supervisor to verify your employment info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: !incomeVerificationProviderActive
          ? SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL
          : SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required'],
      type: 'short_text',
    }),
    hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobSupervisorPhoneNumber',
      title: 'Supervisor Phone',
      label: !incomeVerificationProviderActive && (
        <>
          Sunroom will reach out to your supervisor to verify your employment info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        pattern: '[0-9]*',
        placeholder: '5121234567',
        focusInputOnClickContainer: true,
        className: !incomeVerificationProviderActive
          ? SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL
          : SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'phone'],
      type: 'phone',
    }),
    hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobEmployerEmail',
      title: 'Supervisor Email',
      label: !incomeVerificationProviderActive && (
        <>
          Sunroom will reach out to your supervisor to verify your employment info.
          {' '}
          <SrText weight='bold' color='grey600'>
            Please make sure this info is correct. Your application will be held back if it's not.
          </SrText>
        </>
      ),
      properties: {
        placeholder: defaultTextPlaceholder,
        focusInputOnClickContainer: true,
        className: !incomeVerificationProviderActive
          ? SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL
          : SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'email'],
      type: 'email',
    }),
    hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'currentJobStartDate',
      title: 'Start date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: ['required', 'date'],
      type: 'date_input',
    }),
    {
      id: 'previousJobCompany',
      title: 'Company Name',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required'] : [],
      type: 'short_text',
    },
    {
      id: 'previousJobMailingAddress',
      title: 'Mailing Address',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required'] : [],
      type: 'short_text',
    },
    {
      id: 'previousJobSupervisorName',
      title: 'Supervisor Name',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required'] : [],
      type: 'short_text',
    },
    {
      id: 'previousJobSupervisorPhoneNumber',
      title: 'Supervisor Phone',
      properties: {
        pattern: '[0-9]*',
        placeholder: '5121234567',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required'] : [],
      type: 'phone',
    },
    {
      id: 'previousJobEmployerEmail',
      title: 'Supervisor Email',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required', 'email'] : [],
      type: 'email',
    },
    {
      id: 'previousJobStartDate',
      title: 'Start date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required', 'date'] : [],
      type: 'date_input',
    },
    {
      id: 'previousJobEndDate',
      title: 'End date',
      properties: {
        pattern: '[0-9]*',
        placeholder: 'MM/DD/YYYY',
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
      },
      validations: shouldAskForPreviousEmployment ? ['required', 'date'] : [],
      type: 'date_input',
    },
    hideFieldIf(shouldHideCurrentEmploymentQuestions, {
      id: 'explanationOfIncome',
      title: 'Explain any additional income (optional)',
      label:
        'Example: "I\'m a server, so I make mostly tips. That income is not reflected in my pay stubs. ' +
        'However, I average $40k a year."',
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
      type: 'long_text',
    }),
    {
      id: 'additionalProofOfIncomePhotosCount',
      type: 'hidden',
    },
    {
      id: 'governmentIdPhoto',
      title: 'Government ID',
      label: '📷 Photo of Driver\'s License or Passport',
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: (
          `/renter-application-uploads/${slugify(makeFullName(formValues.firstName, formValues.lastName))}` +
          '-id-{uuid}'
        ),
        isPrivate: true,
        uploadAs: uploadAs,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
    },
    {
      id: 'additionalDocuments',
      title: 'Additional Documents',
      type: 'file_multi',
      properties: {
        fileAccept: '*',
        filePath: (
          `/renter-application-uploads/${slugify(makeFullName(formValues.firstName, formValues.lastName))}` +
          `-add-doc-${(formValues.additionalDocuments || []).length + 1}-{uuid}`
        ),
        isPrivate: true,
        uploadAs: uploadAs,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
      },
    },
  ]);

  getProofOfIncomeQuestions({
    currentJobCompany: formValues.currentJobCompany,
    firstName: renterProfile.firstName,
    lastName: renterProfile.lastName,
    formfieldClassName: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
    uploadAs: uploadAs,
  }).forEach((incomeQuestion) => {
    rentalApplicationFormFields.push(incomeQuestion);
  });

  const additionalProofOfIncomePhotosCount = Math.min(formValues.additionalProofOfIncomePhotosCount || 1);
  for (let index = 0; index < additionalProofOfIncomePhotosCount; index += 1) {
    rentalApplicationFormFields.push({
      id: `additionalProofOfIncomePhotos[${index}]`,
      title: additionalProofOfIncomePhotosCount === 1
        ? 'Additional Proof of Income'
        : `Additional Proof of Income (${index + 1} of ${additionalProofOfIncomePhotosCount})`,
      type: 'file_single',
      properties: {
        fileAccept: '*',
        filePath: makePoiDocFilePath({
          userName: makeFullName(renterProfile.firstName, renterProfile.lastName),
          docNumber: (renterProfile.additionalProofOfIncomePhotos || []).length + 1,
          docType: PROOF_OF_INCOME_ADDITIONAL_DOCUMENTS,
        }),
        warning: <ProofOfIncomeWarningMessage />,
        checkmarks: [
          'Is this uploaded document clear and legible?',
          'Does this document clearly state your legal full name?',
          'Does this document clearly show how much additional income you earn?', // eslint-disable-line max-len
        ],
        widget: (
          <SrBox
            className='fileUploadDelete'
            onClick={() => {
              deleteAdditionalProofOfIncomeFile(index);
            }}
          >
            <SrIcon className='x-close-prompt' />
          </SrBox>
        ),
        className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        isPrivate: true,
        uploadAs: uploadAs,
        minWidth: 800,
        minHeight: 800,
      },
    });
  }

  const documentsCompleted = (
    formValues.offerLetterFileUrl
    || (formValues.additionalProofOfIncomePhotos || [])
      .filter(lodashIdentity).length > 0
    || (formValues.paystubPhotos || [])
      .filter(lodashIdentity).length >= RENTAL_APPLICATION_PAYSTUB_FILES_COUNT
    || (formValues.bankStatementFileUrls || [])
      .filter(lodashIdentity).length >= RENTAL_APPLICATION_BANK_STATEMENT_FILES_COUNT
  );

  const incomeVerificationVOIEIsRequired = (
    incomeVerificationStatus !== INCOME_VERIFICATION_STATUS_COMPLETED
    && !documentsCompleted
    && formValues.hasSourceOfIncome
  );

  rentalApplicationFormFields.push(...[
    {
      id: 'idVerificationMethod',
      type: 'hidden',
      validations: ['required'],
    },
    {
      id: 'incomeVerificationVOIE',
      type: 'hidden',
      validations: incomeVerificationVOIEIsRequired
        ? ['voieCompleted']
        : [],
    },
    {
      id: 'driverLicenseId',
      title: "Driver's License ID",
      properties: {
        placeholder: defaultTextPlaceholder,
        className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL,
        onChange:(evt) => {
          setFieldValue('driverLicenseId', evt.target.value);
          setFieldValue('noDriverLicense', false);
        },
      },
      type: 'hidden',
    },
    {
      id: 'driverLicenseStateIssued',
      title: "Driver's License State",
      properties: {
        choices: stateOptionItems,
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`,
      },
      type: 'hidden',
    },
    {
      id: 'noDriverLicense',
      type: 'hidden',
      properties: {
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} noDriverLicense`,
        children: 'I don\'t have a driver’s license.',
        onChange: (noDriverLicense) => {
          if (noDriverLicense) {
            setFieldValue('driverLicenseId', '');
            setFieldValue('driverLicenseStateIssued', '');
          }
        },
      },
    },
    {
      id: 'childrenCount',
      title: 'Number of Dependents',
      validations: ['required'],
      type: 'select',
      properties: {
        onChange: onChildrenCountChange,
        className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`, // eslint-disable-line max-len
        choices: lodashTimes((index) => ({
          label: index,
          value: index,
        }), maxChildrenCount),
      },
    },
  ]);

  const childrenCount = Math.min(formValues.childrenCount || 0);
  for (let index = 0; index < childrenCount; index += 1) {
    rentalApplicationFormFields.push(
      {
        id: `children[${index}].name`,
        title: 'Name',
        validations: ['required'],
        properties: {
          placeholder: 'Billy Bob',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
        type: 'short_text',
      },
      {
        id: `children[${index}].age`,
        title: 'Age',
        properties: {
          pattern: '[0-9]*',
          placeholder: '12',
          onlyAcceptNumbers: true,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          onShowInviteRoommateModal,
        },
        validations: ['required', 'positiveNumber', 'childAge'],
        type: 'short_text',
      },
      {
        id: `children[${index}].relationship`,
        title: 'Relationship',
        properties: {
          placeholder: 'Daughter',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
        validations: ['required'],
        type: 'short_text',
      },
    );
  }

  rentalApplicationFormFields.push({
    id: 'petCount',
    title: 'Number of Pets',
    validations: ['required', 'maximumPetsAllowed:pets'],
    type: 'select',
    properties: {
      onChange: onPetCountChange,
      className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`, // eslint-disable-line max-len
      choices: lodashTimes((index) => ({
        label: index,
        value: index,
      }), maxPetCount),
      maximumPetsAllowed: listing.petsAllowed
        ? listing.maximumPetsAllowed || 0
        : 0,
    },
  });

  const petCount = Math.min(formValues.petCount || 0);
  for (let index = 0; index < petCount; index += 1) {
    const isESAPet = lodashGet(`pets.${index}.isEsa`, formValues);

    const isDogType = (
      formValues.pets
      && formValues.pets[index]
      && formValues.pets[index].type === PET_OPTION_VALUE_DOG
    );

    rentalApplicationFormFields.push(
      ...lodashCompact([
        {
          id: `pets[${index}].name`,
          title: 'Name',
          properties: {
            placeholder: 'Lucky',
            className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].type`,
          title: 'Type',
          properties: {
            choices: petOptionItems,
            enableResetOption: true,
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
            catsAllowed: listing.catsAllowed,
            dogsAllowed: listing.dogsAllowed,
          },
          validations: ['required', `petType:pets[${index}].isEsa`],
          type: 'select',
        },
        {
          id: `pets[${index}].weight`,
          title: 'Weight',
          properties: {
            pattern: '[0-9]*',
            placeholder: '20',
            className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          },
          validations: ['required'],
          type: 'short_text',
        },
        isDogType && {
          id: `pets[${index}].breed`,
          title: 'Breed',
          properties: {
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
            choices: dogBreedsOptionItems,
            enableResetOption: true,
            breedRestrictions: tenantScreeningCriteria.breedRestrictions || [],
          },
          validations: lodashCompact(['required', !isESAPet && 'petBreed']),
          type: 'select',
        },
        !isDogType && {
          id: `pets[${index}].breed`,
          title: 'Breed',
          properties: {
            placeholder: 'Golden Poodle',
            className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].color`,
          title: 'Color',
          properties: {
            placeholder: 'Black',
            className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].age`,
          title: 'Age',
          properties: {
            placeholder: '3 yrs',
            className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
          },
          validations: ['required'],
          type: 'short_text',
        },
        {
          id: `pets[${index}].gender`,
          title: 'Gender',
          validations: ['required'],
          type: 'select',
          properties: {
            enableResetOption: true,
            choices: [
              {
                label: 'Male',
                value: 'male',
              },
              {
                label: 'Female',
                value: 'female',
              },
            ],
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
          },
        },
        {
          id: `pets[${index}].neutered`,
          title: 'Spayed/neutered',
          validations: ['required'],
          properties: {
            enableResetOption: true,
            transformToBoolean: true,
            choices: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
          },
          type: 'select',
        },
        {
          id: `pets[${index}].declawed`,
          title: 'Declawed',
          validations: ['required'],
          properties: {
            enableResetOption: true,
            transformToBoolean: true,
            choices: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
          },
          type: 'select',
        },
        {
          id: `pets[${index}].rabiesShotsCurrent`,
          title: 'Rabies shots current?',
          validations: ['required'],
          properties: {
            enableResetOption: true,
            transformToBoolean: true,
            choices: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
          },
          type: 'select',
        },
        {
          id: `pets[${index}].isEsa`,
          title: 'ESA / Service Animal?',
          validations: ['required'],
          properties: {
            enableResetOption: true,
            transformToBoolean: true,
            choices: [
              { value: true, label: 'Yes' },
              { value: false, label: 'No' },
            ],
            className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
          },
          type: 'select',
        },
        isESAPet && {
          id: `pets[${index}].esaCertificate`,
          title: 'Upload ESA/Service Document:',
          validations: ['required'],
          type: isESAPet ? 'file_single' : 'hidden',
          properties: {
            fileAccept: '*',
            filePath:
              '/renter-application-uploads/' +
              `${slugify(makeFullName(renterProfile.firstName, renterProfile.lastName))}` +
              `-pets-photo-esa-cert-${index + 1}-{uuid}`,
            className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
            isPrivate: true,
            uploadAs: uploadAs,
            minWidth: 800,
            minHeight: 800,
          },
        },
        {
          id: `pets[${index}].photo`,
          title: 'Photo of pet',
          validations: ['required'],
          type: 'file_single',
          properties: {
            fileAccept: '*',
            filePath: (
              '/renter-application-uploads/' +
              `${slugify(makeFullName(formValues.firstName, formValues.lastName))}` +
              `-pets-photo-${index + 1}-{uuid}`
            ),
            className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
            isPrivate: true,
            uploadAs: uploadAs,
            minWidth: 800,
            minHeight: 800,
          },
        },
      ]),
    );
  }

  rentalApplicationFormFields.push({
    id: 'vehicleCount',
    title: 'Number of Vehicles',
    validations: ['required'],
    type: 'select',
    properties: {
      onChange: onVehicleCountChange,
      className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL} dropdown direction-rtl`, // eslint-disable-line max-len
      choices: lodashTimes((index) => ({
        label: index,
        value: index,
      }), maxVehicleCount),
    },
  });

  const vehicleCount = Math.min(formValues.vehicleCount || 0);
  for (let index = 0; index < vehicleCount; index += 1) {
    rentalApplicationFormFields.push(
      {
        id: `vehicles[${index}].type`,
        title: 'Type',
        properties: {
          enableResetOption: true,
          multipleSelection: false,
          choices: vehicleTypeOptionItems,
          className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
        },
        validations: ['required'],
        type: 'select',
      },
      {
        id: `vehicles[${index}].model`,
        title: 'Make & Model',
        validations: ['required'],
        type: 'short_text',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
      },
      {
        id: `vehicles[${index}].color`,
        title: 'Color',
        validations: ['required'],
        type: 'short_text',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
      },
      {
        id: `vehicles[${index}].year`,
        title: 'Year',
        validations: ['required'],
        type: 'short_text',
        properties: {
          onlyAcceptNumbers: true,
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
      },
      {
        id: `vehicles[${index}].licensePlateNumber`,
        title: 'License Plate Number',
        validations: ['required'],
        type: 'short_text',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
      },
      {
        id: `vehicles[${index}].licenseState`,
        title: 'License State',
        validations: ['required'],
        type: 'select',
        properties: {
          enableResetOption: true,
          choices: stateOptionItems,
          className: `${SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED} dropdown direction-rtl`, // eslint-disable-line max-len
        },
      },
      {
        id: `vehicles[${index}].monthlyPayment`,
        title: 'Monthly Payment',
        validations: ['required'],
        type: 'short_text',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_HORIZONTAL_ADJUSTED,
        },
      },
    );
  }

  rentalApplicationFormFields.push(
    ...lodashCompact([
      {
        id: 'hasWaterFilledFurniture',
        title: 'Will any waterbeds or water-filled furniture be on the Property?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'renterInsurance',
        title: 'Will you maintain renter’s insurance?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'inMilitary',
        title: 'Are you or your spouse, even if separated, in military?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      formValues.inMilitary && {
        id: 'isMilitaryShortStay',
        title:
          'Is the military person serving under orders limiting ' +
          'the military person’s stay to one year or less?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'renterHasPreviouslyBeenEvicted',
        title: 'Have you ever been evicted or broken a lease?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
      },
      formValues.renterHasPreviouslyBeenEvicted && {
        id: 'renterHasPreviouslyBeenEvictedExplanation',
        label: 'Any further details you can provide will help us find the best home for you.',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
        validations: ['required'],
        type: 'long_text',
      },
      formValues.renterHasPreviouslyBeenEvicted && {
        id: 'renterHasPreviouslyBeenEvictedDate',
        title: 'When was the last time you got evicted or broke a lease?',
        properties: {
          pattern: '[0-9]*',
          placeholder: 'MM/DD/YYYY',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
        validations: ['required'],
        type: 'date_input',
      },
      {
        id: 'renterHasPreviouslyBreachedLeaseContract',
        title: 'Have you ever breached a lease or rental agreement?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'renterPreviouslyAskedToMoveOut',
        title: 'Have you ever been asked by a landlord to move out?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'renterPreviouslyFiledForBankruptcy',
        title: 'Have you ever filed for bankruptcy?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      formValues.renterPreviouslyFiledForBankruptcy && {
        id: 'renterPreviousBankruptcyExplanation',
        label:
          'The landlord will want a full explanation. Take as much room as you want to ' +
          'fill this out.\n\nWhen was the bankruptcy? What caused it? What can you tell ' +
          "the landlord, to show that you're not a risk or not as big of a risk as other " +
          'applicants? \n\nThis is your chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
        validations: ['required'],
        type: 'long_text',
      },
      formValues.renterPreviouslyFiledForBankruptcy && {
        id: 'renterPreviouslyFiledForBankruptcyDate',
        title: 'When was the last time you filed for bankruptcy?',
        properties: {
          pattern: '[0-9]*',
          placeholder: 'MM/DD/YYYY',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
        validations: ['required'],
        type: 'date_input',
      },
      {
        id: 'renterPrevioulsyLostPropertyForeclosure',
        title: 'Have you ever lost property in a foreclosure?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'renterPreviouslyHadCreditProblems',
        title: 'Have you had any credit problems, slow-pays or delinquencies?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      formValues.renterPreviouslyHadCreditProblems && {
        id: 'renterPreviousCreditProblemsExplanation',
        label:
          'The landlord will want a full explanation. Take as much room as you want to ' +
          'fill this out.\n\nWhen was your credit lowered? What caused it? What can you ' +
          "tell the landlord, to show that you're not a risk or not as big of a risk as " +
          'other applicants? \n\nThis is your chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
        validations: ['required'],
        type: 'long_text',
      },
      {
        id: 'renterPreviouslyConvitedOfCrime',
        title: 'Have you been convicted of a felony?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
      },
      formValues.renterPreviouslyConvitedOfCrime && {
        id: 'renterPreviousCrimeConvictionExplanation',
        label:
          'The landlord will want a full explanation. Take as much room as you want to ' +
          'fill this out.\n\n When did you get the felony? What caused it? What can you ' +
          "tell the landlord, to show that you're not a risk or not as big of a risk as " +
          'other applicants? \n\nThis is your chance to explain it in a good light.',
        properties: {
          placeholder: 'Write here...',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
        validations: ['required'],
        type: 'long_text',
      },
      formValues.renterPreviouslyConvitedOfCrime && {
        id: 'renterPreviouslyConvitedOfCrimeDate',
        title: 'When was the last time you were convicted of a felony?',
        properties: {
          pattern: '[0-9]*',
          placeholder: 'MM/DD/YYYY',
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
        validations: ['required'],
        type: 'date_input',
      },
      {
        id: 'hasRegisteredSexOffenders',
        title: 'Is any occupant a registered sex offender?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL,
        },
      },
      {
        id: 'hasPendingCriminalMatters',
        title: 'Are there any criminal matters pending against any occupant?',
        validations: ['required'],
        type: 'yes_no',
        properties: {
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
      },
      formValues.hasPendingCriminalMatters && {
        id: 'pendingCriminalMatterExplanation',
        label:
          'The landlord will want a full explanation. Take as much room as you want to fill ' +
          "this out.\n\nWhat caused it? What can you tell the landlord, to show that you're " +
          'not a risk or not as big of a risk as other applicants? \n\nThis is your ' +
          'chance to explain it in a good light.',
        properties: {
          placeholder: defaultTextPlaceholder,
          className: SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL_ADJUSTED,
        },
        validations: ['required'],
        type: 'long_text',
      },
      {
        id: 'agreeTermsOfService',
        title: 'Terms of Service',
        label: (
          <div>
            <div style={{ textAlign: 'justify' }}>
              By electronically signing the Authorization to Release Information hyperlinked below,
              you consent Sunroom Rentals to run an income and rental history verification.
              This Agreement commences on the date of your electronic signature.
            </div>
            <br />
            <div>
              Authorization to Release Information:&nbsp;
              <a
                className='sr-link'
                target='_blank'
                rel='noopener noreferrer'
                href='http://bit.ly/authsunroom'
              >
                http://bit.ly/authsunroom
              </a>
            </div>
          </div>
        ),
        validations: ['legal', 'required'],
        type: 'checkbox',
        properties: {
          className: `${SR_FIELD_THEME_RENTAL_APPLICATION_VERTICAL} legalSwitch`,
        },
      },
    ]),
  );

  const formFields = lodashCompact(
    fieldgroup.reduce((memo, fieldId) => {
      memo.push(
        ...rentalApplicationFormFields.filter(({ id }) => {
          const idWithoutIndexes = id.replace(/\[[^\]]*]/g, '');
          const baseId = idWithoutIndexes.split('.')[0];
          return fieldId === idWithoutIndexes || fieldId === baseId;
        }),
      );
      return memo;
    }, []),
  );

  return formFields;
}
